import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./PopupWithImage.scss";
import Popup from "../Popup/Popup";
import { handlerDataImageForModal, handlerModalWithImage } from "../../redux/Reducers/photoSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getPhotoAlt } from "../../utils/photoAccessibility";
import { useLocation } from "react-router-dom";

type ImageOrientation = "landscape" | "portrait" | "square";

const PopupWithImage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { openModalWithImage, dataForImageModal, getPhotos, showPhotos } = useAppSelector((state) => state.photos);
  const [orientation, setOrientation] = useState<ImageOrientation>("landscape");
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const wheelDistanceRef = useRef(0);
  const wheelLockedUntilRef = useRef(0);
  const wheelResetTimeoutRef = useRef<number | null>(null);
  const lightboxPhotos = pathname.startsWith("/gallery/") ? getPhotos : showPhotos;

  const currentIndex = useMemo(
    () => lightboxPhotos.findIndex((photo) => photo.image === dataForImageModal),
    [dataForImageModal, lightboxPhotos]
  );
  const currentPhoto = currentIndex >= 0 ? lightboxPhotos[currentIndex] : undefined;
  const currentAlt = getPhotoAlt(currentPhoto, currentIndex);
  const canNavigate = lightboxPhotos.length > 1 && currentIndex >= 0;

  const handleClosePopupWithImage = useCallback((): void => {
    dispatch(handlerModalWithImage(false));
    dispatch(handlerDataImageForModal(""));

    window.requestAnimationFrame(() => {
      previouslyFocusedElementRef.current?.focus();
    });
  }, [dispatch]);

  const handleNavigate = useCallback(
    (direction: -1 | 1): void => {
      if (!canNavigate) {
        return;
      }

      const nextIndex = (currentIndex + direction + lightboxPhotos.length) % lightboxPhotos.length;
      dispatch(handlerDataImageForModal(lightboxPhotos[nextIndex].image));
    },
    [canNavigate, currentIndex, dispatch, lightboxPhotos]
  );

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>): void => {
    const { naturalHeight, naturalWidth } = event.currentTarget;
    const ratio = naturalWidth / naturalHeight;

    if (ratio > 1.08) {
      setOrientation("landscape");
    } else if (ratio < 0.92) {
      setOrientation("portrait");
    } else {
      setOrientation("square");
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;

    if (event.pointerType === "mouse" || target.closest("button")) {
      pointerStartRef.current = null;
      return;
    }

    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (pointerStartRef.current === null) {
      return;
    }

    const swipeDistanceX = event.clientX - pointerStartRef.current.x;
    const swipeDistanceY = event.clientY - pointerStartRef.current.y;
    pointerStartRef.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (Math.abs(swipeDistanceX) > 45 && Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY)) {
      handleNavigate(swipeDistanceX < 0 ? 1 : -1);
    }
  };

  const handlePointerCancel = (): void => {
    pointerStartRef.current = null;
  };

  useEffect(() => {
    if (!openModalWithImage) {
      return;
    }

    previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
    closeButtonRef.current?.focus();
  }, [openModalWithImage]);

  useEffect(() => {
    if (!openModalWithImage || !canNavigate) {
      return;
    }

    const previousIndex = (currentIndex - 1 + lightboxPhotos.length) % lightboxPhotos.length;
    const nextIndex = (currentIndex + 1) % lightboxPhotos.length;
    const adjacentImages = [lightboxPhotos[previousIndex].image, lightboxPhotos[nextIndex].image];

    adjacentImages.forEach((imageUrl) => {
      const image = new Image();
      image.src = imageUrl;
    });
  }, [canNavigate, currentIndex, lightboxPhotos, openModalWithImage]);

  useEffect(() => {
    if (!openModalWithImage) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClosePopupWithImage();
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleNavigate(-1);
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        handleNavigate(1);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>("button:not(:disabled)");

      if (!focusableElements?.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClosePopupWithImage, handleNavigate, openModalWithImage]);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!openModalWithImage || !dialog) {
      return;
    }

    const handleTrackpadGesture = (event: WheelEvent): void => {
      if (event.ctrlKey) {
        return;
      }

      event.preventDefault();

      if (!canNavigate || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }

      if (wheelResetTimeoutRef.current !== null) {
        window.clearTimeout(wheelResetTimeoutRef.current);
      }

      wheelResetTimeoutRef.current = window.setTimeout(() => {
        wheelDistanceRef.current = 0;
      }, 180);

      if (Date.now() < wheelLockedUntilRef.current) {
        return;
      }

      wheelDistanceRef.current += event.deltaX;

      if (Math.abs(wheelDistanceRef.current) >= 60) {
        handleNavigate(wheelDistanceRef.current > 0 ? 1 : -1);
        wheelDistanceRef.current = 0;
        wheelLockedUntilRef.current = Date.now() + 450;
      }
    };

    dialog.addEventListener("wheel", handleTrackpadGesture, { passive: false });

    return () => {
      dialog.removeEventListener("wheel", handleTrackpadGesture);

      if (wheelResetTimeoutRef.current !== null) {
        window.clearTimeout(wheelResetTimeoutRef.current);
        wheelResetTimeoutRef.current = null;
      }
    };
  }, [canNavigate, handleNavigate, openModalWithImage]);

  if (!openModalWithImage || !dataForImageModal) {
    return null;
  }

  return (
    <Popup onClick={handleClosePopupWithImage} openPopup={openModalWithImage} allowPinchZoom>
      <div
        ref={dialogRef}
        className={`popup-image__container popup-image__container_${orientation}`}
        role="dialog"
        aria-modal="true"
        aria-label="Photography gallery viewer"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <span className="popup-image__counter" aria-live="polite">
          {currentIndex >= 0 ? currentIndex + 1 : 1} / {lightboxPhotos.length || 1}
        </span>
        <button
          ref={closeButtonRef}
          className="popup-image__closeBtn"
          type="button"
          aria-label="Close photograph viewer"
          onClick={handleClosePopupWithImage}
        />
        <button
          className="popup-image__navigation popup-image__navigation_previous"
          type="button"
          aria-label="Previous photograph"
          disabled={!canNavigate}
          onClick={() => handleNavigate(-1)}
        >
          <span aria-hidden="true">←</span>
        </button>
        <figure className="popup-image__figure">
          <img
            className="popup-image__image"
            src={dataForImageModal}
            alt={currentAlt}
            decoding="async"
            draggable="false"
            onLoad={handleImageLoad}
          />
        </figure>
        <button
          className="popup-image__navigation popup-image__navigation_next"
          type="button"
          aria-label="Next photograph"
          disabled={!canNavigate}
          onClick={() => handleNavigate(1)}
        >
          <span aria-hidden="true">→</span>
        </button>
        <span className="popup-image__swipeHint">Swipe or use arrows</span>
      </div>
    </Popup>
  );
};

export default PopupWithImage;
